import type {
  ClassroomEntry,
  DashboardView,
  FamilyDataActionInput,
  FamilyDataCorrectionInput,
  FamilyDataControlsView,
  FamilyDataRequestReceipt,
  FamilySession,
  InviteLoginInput,
  LearningTrialView,
  MasteryEvidence,
  MathosWebApi,
  ParentEvidenceView,
  RetentionView,
  StudentAnswerPayload,
  SubmitAnswerResult,
  TeacherTurnEnvelope,
  VerificationResult,
} from "./contracts";
import { hasCurrentConsentVersions } from "../compliance/consent-documents";
import {
  createInitialRecord,
  createNextTeacherTurn,
  createTransferEvidence,
  nextDemoActivityIndex,
  normalizeDemoAnswer,
  verifyDemoAnswer,
} from "./demo-classroom-engine";
import { demoLessons, findDemoLesson } from "./demo-lessons";
import {
  CHILD_ID,
  HOUSEHOLD_ID,
  RETENTION_ID,
  availabilityFor,
  classroomFromRecord,
  demoDelay,
  demoRetentionTask,
  readDemoState,
  requireDemoFamily,
  publicDemoLesson,
  sampleEvidence,
  toLessonListItem,
  writeDemoState,
  type DemoSessionRecord,
} from "./demo-state";

function studentEntry(
  answer: StudentAnswerPayload,
  verification: VerificationResult,
  phase: ClassroomEntry["phase"],
  independence: NonNullable<ClassroomEntry["independence"]>,
): ClassroomEntry {
  return {
    id: crypto.randomUUID(),
    kind: "student",
    phase,
    independence,
    occurredAt: answer.submittedAt,
    studentAnswer: answer,
    verification,
  };
}

function teacherEntry(
  turn: TeacherTurnEnvelope,
): ClassroomEntry {
  return {
    id: turn.turnId,
    kind: "teacher",
    phase: turn.phase,
    occurredAt: new Date().toISOString(),
    teacherTurn: {
      ...turn,
      visualOperations: turn.visualOperations ?? [],
    },
  };
}

function updateSessionStatus(
  sessionId: string,
  status: "active" | "paused",
  pauseCause: DemoSessionRecord["pauseCause"] = "manually_paused",
): DemoSessionRecord {
  requireDemoFamily();
  const state = readDemoState();
  const record = state.sessions[sessionId];
  if (!record) throw new Error("课堂没有找到。");
  const updated: DemoSessionRecord = {
    ...record,
    snapshot: {
      ...record.snapshot,
      status,
      version: record.snapshot.version + 1,
      updatedAt: new Date().toISOString(),
    },
    ...(status === "paused" ? { pauseCause } : {}),
  };
  if (status === "active") {
    delete updated.pauseCause;
  }
  writeDemoState({
    ...state,
    sessions: { ...state.sessions, [sessionId]: updated },
  });
  return updated;
}

async function submitDemoAnswer(
  sessionId: string,
  answer: StudentAnswerPayload,
): Promise<SubmitAnswerResult> {
  requireDemoFamily();
  const state = readDemoState();
  const record = state.sessions[sessionId];
  if (!record) throw new Error("课堂没有找到，答案尚未发送。");
  const duplicate = record.entries.find(
    (entry) => entry.studentAnswer?.idempotencyKey === answer.idempotencyKey,
  );
  if (duplicate) {
    const duplicateVerification =
      duplicate.verification ??
      verifyDemoAnswer(
        duplicate.studentAnswer!.activityId,
        duplicate.studentAnswer!,
      );
    return demoDelay({
      classroom: classroomFromRecord(record),
      verification: duplicateVerification,
    });
  }
  if (record.snapshot.status !== "active") {
    throw new Error("课堂已暂停，答案保留在本机；恢复后再发送。");
  }

  const verification = verifyDemoAnswer(answer.activityId, answer);
  const response = createNextTeacherTurn(record, answer, verification);
  const completed =
    response.stateIntent === "complete" && verification.mayAdvance;
  const activityIndex = nextDemoActivityIndex(record, verification);
  const lesson = findDemoLesson(record.snapshot.lessonId);
  const answeredActivity = lesson.activities[record.snapshot.activityIndex]!;
  const nextPhase = completed
    ? "summary"
    : lesson.activities[activityIndex]!.phase;
  const evidence =
    completed && record.snapshot.lessonId === "L5"
      ? [
          ...record.evidence,
          createTransferEvidence(record, answer, verification),
        ]
      : record.evidence;
  const updated: DemoSessionRecord = {
    snapshot: {
      ...record.snapshot,
      status: completed ? "completed" : "active",
      phase: nextPhase,
      activityIndex,
      version: record.snapshot.version + 1,
      lastEventSequence: record.snapshot.lastEventSequence + 3,
      updatedAt: new Date().toISOString(),
    },
    entries: [
      ...record.entries,
      studentEntry(
        answer,
        verification,
        record.snapshot.phase,
        answeredActivity.independence,
      ),
      teacherEntry(response),
    ],
    evidence,
    answerReceipt: {
      idempotencyKey: answer.idempotencyKey,
      savedAt: new Date().toISOString(),
    },
  };
  writeDemoState({
    ...state,
    sessions: { ...state.sessions, [sessionId]: updated },
  });
  return demoDelay({
    classroom: classroomFromRecord(updated),
    verification,
  });
}

function createRetentionEvidence(
  answer: StudentAnswerPayload,
): MasteryEvidence {
  const normalized = normalizeDemoAnswer(answer);
  const correct =
    normalized.includes("405") &&
    (normalized.includes("3") || normalized.includes("45"));
  return {
    evidenceId: crypto.randomUUID(),
    sessionId: "d3b142aa-2a2e-43fa-893d-793d7d46a8b1",
    childId: CHILD_ID,
    lessonId: "L24",
    activityId: "L24-retention",
    attemptNumber: 1,
    kind: "retention",
    studentAnswer: answer,
    verification: {
      outcome: correct ? "correct" : "incorrect",
      normalizedAnswer: normalized,
      reasons: [
        correct
          ? "公比和第 5 项都写对，过程可核对。"
          : "需要同时写出公比和第 5 项。",
      ],
      matchedClaims: correct ? ["公比为3", "第5项为405"] : [],
      missingClaims: correct ? [] : ["公比或第5项"],
      forbiddenClaims: [],
      mayAdvance: correct,
      needsTeacherClarification: !correct,
      verifier: "demo-retention-verifier",
    },
    teacherHelpAfterPrompt: false,
    recordedAt: new Date().toISOString(),
  };
}

export function createDemoAdapter(
  options: { dataControlsEnabled?: boolean } = {},
): MathosWebApi {
  return {
    getSession: () => demoDelay(readDemoState().family, 80),
    async acceptInvite(input: InviteLoginInput) {
      if (!hasCurrentConsentVersions(input.consent)) {
        throw new Error("同意文件版本已更新，请重新阅读并确认。");
      }
      if (input.inviteCode.trim().length < 6) {
        throw new Error("邀请码无效，请向课程管理员核对。");
      }
      const family: FamilySession = {
        householdId: HOUSEHOLD_ID,
        parentName: input.parentName.trim(),
        childId: CHILD_ID,
        childName: input.childName.trim(),
        expiresAt: "2026-08-19T23:59:59+08:00",
      };
      const state = readDemoState();
      writeDemoState({
        ...state,
        family,
        childBirthYear: input.childBirthYear,
      });
      return demoDelay(family);
    },
    async signIn() {
      return demoDelay(requireDemoFamily());
    },
    async signOut() {
      const state = readDemoState();
      writeDemoState({ ...state, family: null });
      return demoDelay(undefined, 80);
    },
    async getDashboard() {
      const family = requireDemoFamily();
      const state = readDemoState();
      const lessons = demoLessons.map((lesson) =>
        toLessonListItem(lesson.id, state.sessions),
      );
      const currentLesson =
        lessons.find((lesson) => lesson.availability === "in_progress") ??
        lessons.find((lesson) => lesson.availability === "ready");
      const view: DashboardView = {
        family,
        greeting: `${family.childName}，今天先把一个数学结构讲透。`,
        ...(currentLesson ? { currentLesson } : {}),
        nextRetentionTaskId: RETENTION_ID,
        lessons,
        stats: {
          completedLessons: lessons.filter(
            (lesson) => lesson.availability === "completed",
          ).length,
          independentEvidence: 1,
          retentionDue: 1,
        },
      };
      return demoDelay(view);
    },
    async getLesson(lessonId: string) {
      requireDemoFamily();
      const state = readDemoState();
      const lesson = findDemoLesson(lessonId);
      const session = Object.values(state.sessions).find(
        (candidate) => candidate.snapshot.lessonId === lessonId,
      );
      return demoDelay({
        lesson: publicDemoLesson(lesson.id),
        catalogPosition: toLessonListItem(lesson.id, state.sessions).catalogPosition,
        availability: availabilityFor(lessonId, state.sessions),
        prerequisiteNote: "能读懂花括号中的对象，并能比较具体数是否相同。",
        learningGoals: [
          "讲清当前数学对象与关系",
          "能说明判断成立的理由",
          "独立完成一题相近迁移",
        ],
        misconceptionPreview: [
          "把元素与集合的位置写反",
          "把“属于”与“包含”混为一谈",
        ],
        sourceNotice:
          "当前是界面演示占位内容：未导入原教材，也未完成授权或 canonical 校订，不能作为发布教材或正式学习证据。",
        ...(session ? { existingSessionId: session.snapshot.sessionId } : {}),
      });
    },
    async startLesson(lessonId: string) {
      requireDemoFamily();
      const state = readDemoState();
      const existing = Object.values(state.sessions).find(
        (candidate) => candidate.snapshot.lessonId === lessonId,
      );
      if (existing) return demoDelay(classroomFromRecord(existing));
      const record = createInitialRecord(lessonId);
      writeDemoState({
        ...state,
        sessions: { ...state.sessions, [record.snapshot.sessionId]: record },
      });
      return demoDelay(classroomFromRecord(record));
    },
    async getClassroom(sessionId: string) {
      requireDemoFamily();
      const record = readDemoState().sessions[sessionId];
      if (!record) throw new Error("这节课堂不存在或不属于当前家庭。");
      return demoDelay(classroomFromRecord(record), 100);
    },
    submitAnswer: submitDemoAnswer,
    async pauseSession(sessionId: string) {
      return demoDelay(
        classroomFromRecord(
          updateSessionStatus(sessionId, "paused", "manually_paused"),
        ),
      );
    },
    async resumeSession(sessionId: string) {
      return demoDelay(
        classroomFromRecord(updateSessionStatus(sessionId, "active")),
      );
    },
    async getParentEvidence(lessonId: string): Promise<ParentEvidenceView> {
      const family = requireDemoFamily();
      const state = readDemoState();
      const session = Object.values(state.sessions).find(
        (candidate) => candidate.snapshot.lessonId === lessonId,
      );
      const evidence =
        session?.evidence.length ? session.evidence : [sampleEvidence(lessonId)];
      return demoDelay({
        family,
        lesson: toLessonListItem(lessonId, state.sessions),
        evidence,
        misconceptionSummary:
          "孩子已经能区分“看起来有规律”和“按定义逐项核对”，仍需留意书写对象与关系的位置。",
        teacherSummary:
          "本课关键方法已在独立迁移中出现。建议按计划完成一次延迟复测，确认不是短时记忆。",
        retentionTask: demoRetentionTask(
          state.retentionEvidence ? "completed" : "available",
        ),
      });
    },
    async getRetention(taskId: string): Promise<RetentionView> {
      const family = requireDemoFamily();
      if (taskId !== RETENTION_ID) throw new Error("复测任务不存在。");
      const state = readDemoState();
      return demoDelay({
        family,
        lesson: toLessonListItem("L24", state.sessions),
        task: demoRetentionTask(
          state.retentionEvidence ? "completed" : "available",
        ),
        activityId: "L24-retention",
        answerKind: "structured",
        prompt:
          "数列 5，15，45，… 的公比是多少？请写出第 5 项，并用一步式子说明。",
        ...(state.retentionEvidence
          ? { completedEvidence: state.retentionEvidence }
          : {}),
      });
    },
    async submitRetention(
      taskId: string,
      answer: StudentAnswerPayload,
    ): Promise<RetentionView> {
      const family = requireDemoFamily();
      if (taskId !== RETENTION_ID) throw new Error("复测任务不存在。");
      const evidence = createRetentionEvidence(answer);
      const state = readDemoState();
      writeDemoState({ ...state, retentionEvidence: evidence });
      return demoDelay({
        family,
        lesson: toLessonListItem("L24", state.sessions),
        task: demoRetentionTask("completed"),
        activityId: "L24-retention",
        answerKind: "structured",
        prompt:
          "数列 5，15，45，… 的公比是多少？请写出第 5 项，并用一步式子说明。",
        completedEvidence: evidence,
      });
    },
    async getFamilyDataControls(): Promise<FamilyDataControlsView> {
      const family = requireDemoFamily();
      const available = options.dataControlsEnabled === true;
      return demoDelay({
        children: [
          {
            childId: family.childId,
            displayName: family.childName,
            birthYear: readDemoState().childBirthYear ?? 2013,
          },
        ],
        correctData: {
          availability: available ? "available" : "controlled_unavailable",
          reason: available
            ? "浏览器验收模式会执行隔离的本机档案更正。"
            : "演示 adapter 未连接正式档案更正服务。",
        },
        exportData: {
          availability: available ? "available" : "controlled_unavailable",
          reason: available
            ? "浏览器验收模式会生成隔离的本机导出回执。"
            : "演示 adapter 未连接正式数据导出服务。",
        },
        deleteData: {
          availability: available ? "available" : "controlled_unavailable",
          reason: available
            ? "浏览器验收模式会删除隔离的本机演示记录。"
            : "演示 adapter 未连接正式数据删除服务。",
        },
      });
    },
    async requestFamilyDataCorrection(input: FamilyDataCorrectionInput) {
      if (!options.dataControlsEnabled) {
        throw new Error("档案更正当前受控未启用，未创建任何请求。");
      }
      requireDemoPassword(input.password);
      const state = readDemoState();
      const family = requireDemoFamily();
      if (input.childId !== family.childId) {
        throw new Error("该学生资料不可用");
      }
      writeDemoState({
        ...state,
        family: { ...family, childName: input.displayName },
        childBirthYear: input.birthYear,
      });
      return demoDelay(dataReceipt("correct"));
    },
    async requestFamilyDataExport(input: FamilyDataActionInput) {
      if (!options.dataControlsEnabled) {
        throw new Error("数据导出当前受控未启用，未创建任何请求。");
      }
      requireDemoPassword(input.password);
      if (input.confirmationText !== "导出本家庭学习数据") {
        throw new Error("导出确认文字不正确");
      }
      return demoDelay(dataReceipt("export"));
    },
    async requestFamilyDataDeletion(input: FamilyDataActionInput) {
      if (!options.dataControlsEnabled) {
        throw new Error("数据删除当前受控未启用，未创建任何请求。");
      }
      requireDemoPassword(input.password);
      if (input.confirmationText !== "删除本家庭全部学习数据") {
        throw new Error("删除确认文字不正确");
      }
      writeDemoState({ family: null, sessions: {} });
      return demoDelay(dataReceipt("delete"));
    },
    async getLearningTrial(childId: string): Promise<LearningTrialView> {
      const family = requireDemoFamily();
      if (childId !== family.childId) throw new Error("该学生资料不可用");
      return demoDelay(unavailableDemoLearningTrial(family));
    },
    async startLearningTrial() {
      throw new Error("界面演示记录不能计入孩子本人的家庭试学证据。");
    },
  };
}

function unavailableDemoLearningTrial(
  family: FamilySession,
): LearningTrialView {
  return {
    child: { childId: family.childId, displayName: family.childName },
    status: "not_started",
    canStart: false,
    message: "当前是界面演示，不能开始真实家庭试学记录。",
    excludedPreviousLessonCount: 0,
    lessonPlan: [],
    targets: {
      completedLessons: 5,
      lessonFamilies: 5,
      immediatePassRate: 0.8,
      retentionPassRate: 0.7,
    },
    progress: {
      attemptedLessons: 0,
      completedLessons: 0,
      lessonFamilies: 0,
      immediatePassedLessons: 0,
      immediatePassRate: null,
      structuredRetellLessons: 0,
      retentionEligibleLessons: 0,
      retentionPassedLessons: 0,
      retentionPassRate: null,
      waitingForRetentionLessons: 0,
      lessonIds: [],
    },
  };
}

function requireDemoPassword(password: string): void {
  if (password !== "family-demo-password") {
    throw new Error("家长密码不正确");
  }
}

function dataReceipt(
  action: "correct" | "export" | "delete",
): FamilyDataRequestReceipt {
  return {
    requestId: crypto.randomUUID(),
    action,
    status: "accepted",
    submittedAt: new Date().toISOString(),
  };
}
